import React, { lazy } from 'react';
import { createBrowserRouter, Outlet, Navigate } from 'react-router-dom';
import { AssessmentProvider } from './contexts/AssessmentContext';
import ResultsViewWrapper from './components/results/ResultsViewWrapper';
import IndustryAssessmentPreview from './components/industry/IndustryAssessmentPreview';
import RouteChangeTracker from './components/common/RouteChangeTracker';
import DevResultsSanity from './components/results/DevResultsSanity';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import { isDevToolsEnabled } from './utils/devMode';
import LaunchPage from './components/launch/LaunchPage';

// Render-time gate for dev-only routes: redirects real users to the homepage.
// Evaluated when the route renders (after index.js has processed ?dev=1), so the
// per-device opt-in works without a rebuild.
const DevOnly = ({ children }) => (isDevToolsEnabled() ? children : <Navigate to="/" replace />);

// Lazy-loaded components
const Home = lazy(() => import('./pages/Home'));
// Deprecated external registration page; unified inside assessment flow
// const Registration = lazy(() => import('./components/registration/Registration'));
const Assessment = lazy(() => import('./components/assessment/Assessment'));
// const Introduction = lazy(() => import('./components/introduction/Introduction'));
const PaymentWall = lazy(() => import('./components/payment/PaymentWall'));
const PaymentTest = lazy(() => import('./components/payment/PaymentTest'));
const PDFGenerator = lazy(() => import('./components/results/PDFGenerator'));
const NotFound = lazy(() => import('./components/common/NotFound'));
const CoachPage = lazy(() => import('./components/coach/CoachPage'));

// Cosmic Assessment Components
const CosmicRegistration = lazy(() => import('./components/cosmic-assessment/CosmicRegistration'));
const CosmicLogin = lazy(() => import('./components/cosmic-assessment/CosmicLogin'));
const CosmicUserPortal = lazy(() => import('./components/cosmic-assessment/CosmicUserPortal'));
const CosmicCoachAccess = lazy(() => import('./components/cosmic-assessment/CosmicCoachAccess'));
const CosmicPaymentWall = lazy(() => import('./components/cosmic-assessment/CosmicPaymentWall'));
const CosmicAssessmentFlow = lazy(() => import('./components/cosmic-assessment/CosmicAssessmentFlow'));
const CosmicSharedResult = lazy(() => import('./components/results/cosmic/CosmicSharedResult'));

// Coach Directory
const CosmicCoachDirectory = lazy(() => import('./components/coach-directory/CosmicCoachDirectory'));

// Motivated Skills tool (coach-driven card sort; APC-branded)
const MotivatedSkillsFlow = lazy(() => import('./components/motivated-skills/MotivatedSkillsFlow'));
const MotivatedSkillsResults = lazy(() => import('./components/motivated-skills/MotivatedSkillsResults'));
const MotivatedSkillsPreview = lazy(() => import('./components/motivated-skills/MotivatedSkillsPreview'));
const WorkStyleFlow = lazy(() => import('./components/work-style/WorkStyleFlow'));
const WorkStyleResultsPage = lazy(() => import('./components/work-style/WorkStyleResultsPage'));
const WorkStylePreview = lazy(() => import('./components/work-style/WorkStylePreview'));

// Legal pages
const PrivacyPolicy = lazy(() => import('./components/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/legal/TermsOfService'));

// Opt-in door (Mailflow 5.3 / Glasshouse builder/AI/ADHD stream)
const OptInDoor = lazy(() => import('./components/optin/OptInDoor'));

// Testing Components
const ResultsTestPage = lazy(() => import('./components/testing/ResultsTestPage'));
const DevNavigationHelper = lazy(() => import('./components/testing/DevNavigationHelper'));

// Root layout component with ErrorBoundary - no provider (cosmic routes don't need it)
const RootLayout = () => {
	return (
		<ErrorBoundary>
			<RouteChangeTracker />
			<DevNavigationHelper />
			<Outlet />
		</ErrorBoundary>
	);
};

const devChildRoutes = [];
if (process.env.NODE_ENV !== 'production') {
	devChildRoutes.push({ path: 'dev/results-sanity', element: <DevResultsSanity /> });
}

// Create router with proper configuration
export const router = createBrowserRouter([
	// S5b.2: into an assessment from the client's journey. Its own top-level route,
	// outside RootLayout, so the page tracker never records `?t=`: the page strips
	// the ticket first, and analytics starts on the assessment it routes to.
	{ path: '/launch', element: <ErrorBoundary><LaunchPage /></ErrorBoundary> },
	{
		path: '/',
		element: <RootLayout />,
		children: [
			{ index: true, element: <Home /> },
			// { path: 'intro', element: <Introduction /> },
			{ path: 'register', element: <Navigate to="/assessment?step=registration" replace /> },
			
			// Legacy Assessment Routes (wrap with provider only if needed)
			{ 
				path: 'assessment', 
				element: (
					<AssessmentProvider>
						<Assessment />
					</AssessmentProvider>
				) 
			},
			{ 
				path: 'payment', 
				element: (
					<AssessmentProvider>
						<PaymentWall />
					</AssessmentProvider>
				) 
			},
			{ 
				path: 'coach', 
				element: (
					<AssessmentProvider>
						<CoachPage />
					</AssessmentProvider>
				) 
			},
			
			// Cosmic Assessment Routes (no provider needed)
			{ path: 'cosmic-flow', element: <CosmicAssessmentFlow /> },
			{ path: 'cosmic-registration', element: <CosmicRegistration /> },
			{ path: 'cosmic-login', element: <CosmicLogin /> },
			{ path: 'cosmic-portal', element: <ProtectedRoute><CosmicUserPortal /></ProtectedRoute> },
			{ path: 'cosmic-coach-access', element: <CosmicCoachAccess /> },
			{ path: 'cosmic-payment', element: <CosmicPaymentWall /> },
			{ path: 'cosmic-assessment', element: <CosmicAssessmentFlow /> },

			// Public, read-only shared result (unguessable token; no auth required)
			{ path: 'r/:shareId', element: <CosmicSharedResult /> },
			
			// Shared Routes - Results needs AssessmentProvider for cosmic components
			{ 
				path: 'results', 
				element: (
					<AssessmentProvider>
						<ResultsViewWrapper />
					</AssessmentProvider>
				) 
			},
			{ path: 'payment-test', element: <PaymentTest /> },
			{ path: 'report', element: <PDFGenerator /> },
			{ path: 'industry-preview', element: <IndustryAssessmentPreview /> },
			{ path: 'coach-directory', element: <CosmicCoachDirectory /> },

			// Motivated Skills tool (client flow + coach deep-link read)
			{ path: 'motivated-skills', element: <MotivatedSkillsFlow /> },
			{ path: 'motivated-skills/results', element: <MotivatedSkillsResults /> },
			{ path: 'motivated-skills/preview', element: <DevOnly><MotivatedSkillsPreview /></DevOnly> },
			{ path: 'work-style', element: <WorkStyleFlow /> },
			{ path: 'work-style/results', element: <WorkStyleResultsPage /> },
			{ path: 'work-style/preview', element: <DevOnly><WorkStylePreview /></DevOnly> },

			// Legal
			{ path: 'privacy', element: <PrivacyPolicy /> },
			{ path: 'terms', element: <TermsOfService /> },

			// Opt-in door (Mailflow 5.3 / Glasshouse builder/AI/ADHD stream)
			{ path: 'workshop', element: <OptInDoor /> },
			
			// Testing Routes (dev-gated; real users are redirected home)
			{ 
				path: 'results-test', 
				element: (
					<AssessmentProvider>
						<DevOnly><ResultsTestPage /></DevOnly>
					</AssessmentProvider>
				) 
			},
			
			// Legacy coach access redirect
			{ path: 'coach-access', element: <Navigate to="/cosmic-coach-access" replace /> },
			...devChildRoutes,
			{ path: '*', element: <NotFound /> },
		],
	},
]); 
